import Fuse from 'fuse.js';
import { ENTITY } from './constants';

// ─── Utility functions ───────────────────────────────

export const log = (level, ...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console[level](...args);
  }
};

export const scrollToTop = () => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
};

const normalizeForSearch = (str) => {
  if (!str) return '';
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const includesNormalized = (text, search) => {
  const normalizedText = normalizeForSearch(text).replace(/ñ/g, 'n');
  const normalizedSearch = normalizeForSearch(search).replace(/ñ/g, 'n');
  return normalizedText.includes(normalizedSearch);
};

/**
 * Extrae todas las tags únicas de los posts
 */
export const extractAllTags = (posts, featuredTags) => {
  const tags = new Set();
  for (let i = 0; i < posts.length; i++) {
    const postTags = posts[i].tags;
    for (let j = 0; j < postTags.length; j++) {
      tags.add(postTags[j]);
    }
  }
  const featured = featuredTags.filter(tag => tag === 'Todos' || tags.has(tag));
  const others = Array.from(tags).filter(tag => !featuredTags.includes(tag)).sort();
  return [...featured, ...others];
};

/**
 * Genera sugerencias de autocompletado
 */
export const generateAutocompleteSuggestions = (inputValue, posts, maxSuggestions = 5) => {
  if (!inputValue) return [];

  const suggestions = [];
  const seen = new Set();
  const cleanedField = ENTITY.dataFields.cleanedItems;

  // 1. Search in cleaned items first
  for (let i = 0; i < posts.length && suggestions.length < maxSuggestions; i++) {
    const items = posts[i][cleanedField] || [];
    for (let j = 0; j < items.length && suggestions.length < maxSuggestions; j++) {
      const item = items[j];
      if (includesNormalized(item, inputValue) && !seen.has(item)) {
        suggestions.push(item);
        seen.add(item);
      }
    }
  }

  // 2. Search in names
  if (suggestions.length < maxSuggestions) {
    for (let i = 0; i < posts.length && suggestions.length < maxSuggestions; i++) {
      const name = posts[i].name;
      if (includesNormalized(name, inputValue) && !seen.has(name)) {
        suggestions.push(name);
        seen.add(name);
      }
    }
  }

  // 3. Search in tags
  if (suggestions.length < maxSuggestions) {
    for (let i = 0; i < posts.length && suggestions.length < maxSuggestions; i++) {
      const tags = posts[i].tags;
      for (let j = 0; j < tags.length && suggestions.length < maxSuggestions; j++) {
        const tag = tags[j];
        if (includesNormalized(tag, inputValue) && !seen.has(tag)) {
          suggestions.push(tag);
          seen.add(tag);
        }
      }
    }
  }

  return suggestions;
};

const STOPWORDS = new Set([
  'de', 'la', 'el', 'en', 'y', 'a', 'al', 'con', 'por', 'para',
  'un', 'una', 'las', 'los', 'del', 'su', 'que', 'es', 'e', 'o',
  'le', 'lo', 'se', 'no', 'tu', 'mi', 'me', 'te', 'nos', 'les',
  'como', 'mas', 'pero', 'sin', 'entre', 'todo', 'esta', 'este',
  'muy', 'que', 'tan', 'cuando', 'donde', 'porque'
]);

const normalizeForFuse = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ñ/g, 'n');
};

/**
 * Filtra posts con fuzzy search (Fuse.js)
 */
export const fuzzyFilterPosts = (posts, searchTerm, selectedTag, showEasyOnly) => {
  let filtered = posts;
  if (selectedTag !== 'Todos') {
    filtered = filtered.filter(p => p.tags && p.tags.includes(selectedTag));
  }
  if (showEasyOnly) {
    filtered = filtered.filter(p => p.easy);
  }

  if (!searchTerm || !searchTerm.trim()) return filtered;

  const cleanedField = ENTITY.dataFields.cleanedItems;

  const tokens = searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const significantTokens = tokens.filter(t => t.length >= 2 && !STOPWORDS.has(t));

  if (significantTokens.length === 0) {
    return filtered.filter(post =>
      tokens.some(t =>
        includesNormalized(post.name, t) ||
        (post[cleanedField] || []).some(item => includesNormalized(item, t)) ||
        (post.tags || []).some(tag => includesNormalized(tag, t))
      )
    );
  }

  const matchingIdSets = significantTokens.map(token => {
    const threshold = Math.min(0.1 + (token.length - 1) * 0.035, 0.45);
    const fuse = new Fuse(filtered, {
      keys: [
        { name: 'name', getFn: (item) => normalizeForFuse(item.name) },
        { name: 'tags', getFn: (item) => (item.tags || []).map(t => normalizeForFuse(t)) },
        {
          name: 'cleanedItems',
          getFn: (item) => (item[cleanedField] || []).map(i => normalizeForFuse(i))
        },
      ],
      threshold,
    });
    return new Set(fuse.search(normalizeForFuse(token)).map(r => r.item.id));
  });

  return filtered.filter(post =>
    matchingIdSets.every(idSet => idSet.has(post.id))
  );
};

/**
 * Obtiene posts relacionados
 */
export const findRelatedPosts = (posts, post) => {
  if (!post || !post.related_posts) return [];
  const related = [];
  for (const rel of post.related_posts) {
    const found = posts.find(p => p.id === rel.post_id || p.id === rel.recipe_id);
    if (found) related.push(found);
  }
  return related;
};

/**
 * Obtiene URL embed de Instagram
 */
export const getInstagramEmbedUrl = (url) => {
  if (!url) return null;
  const isReel = url.includes('/reel/');
  const isPost = url.includes('/p/');
  if (isReel || isPost) return `${url}embed/`;
  return null;
};

export const getInstagramLinkUrl = (instagramUrl, shortcode, isMobile) => {
  if (!instagramUrl || !shortcode) return null;
  if (isMobile) return `instagram://media?id=${shortcode}`;
  return instagramUrl;
};

export const getInstagramProfileUrl = (username, isMobile) => {
  if (isMobile) return `instagram://user?username=${username}`;
  return `https://www.instagram.com/${username}/`;
};

export const getFacebookProfileUrl = (username, pageId, isMobile) => {
  if (isMobile) return `fb://page/${pageId}`;
  return `https://www.facebook.com/${username}/`;
};

/**
 * Paginación
 */
export const paginateArray = (items, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  return items.slice(startIndex, startIndex + itemsPerPage);
};

export const generatePageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const delta = 1;

  pages.push(1);

  if (totalPages <= 7) {
    for (let i = 2; i <= totalPages; i++) pages.push(i);
  } else {
    if (currentPage <= 3) {
      for (let i = 2; i <= 4; i++) pages.push(i);
      pages.push('ellipsis-1');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push('ellipsis-1');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push('ellipsis-1');
      for (let i = currentPage - delta; i <= currentPage + delta; i++) pages.push(i);
      pages.push('ellipsis-2');
      pages.push(totalPages);
    }
  }

  return pages;
};
